import {
  itemsService,
  categoriesService,
  locationsService,
  usersService,
  dashboardService,
  storageService,
  merchantsService,
} from '@catalog/supabase';
import { Item, Category, Location, User, Merchant } from '@catalog/types';

// Type conversion helpers to map Supabase types to app types
const convertSupabaseItem = (item: any): Item => ({
  ...item,
  description: item.description ?? undefined,
  current_value: item.current_value ?? undefined,
  notes: item.notes ?? undefined,
  photo_url: item.photo_url ?? undefined,
  merchant_id: item.merchant_id ?? undefined,
});

const convertSupabaseCategory = (category: any): Category => ({
  ...category,
  description: category.description ?? undefined,
});

const convertSupabaseMerchant = (merchant: any): Merchant => ({
  ...merchant,
  url: merchant.url ?? undefined,
  website: merchant.website ?? undefined,
  image_url: merchant.image_url ?? undefined,
});

// Supabase-only API - no mock fallbacks
export const supabaseApi = {
  // Storage API
  storage: {
    uploadItemPhoto: async (file: File, itemId: string): Promise<string | null> => {
      return await storageService.uploadItemPhoto(file, itemId);
    },

    uploadItemPhotos: async (files: File[], itemId: string): Promise<string[]> => {
      return await storageService.uploadItemPhotos(files, itemId);
    },

    deleteItemPhoto: async (photoUrl: string): Promise<boolean> => {
      return await storageService.deleteItemPhoto(photoUrl);
    },

    deleteItemPhotos: async (photoUrls: string[]): Promise<boolean> => {
      return await storageService.deleteItemPhotos(photoUrls);
    }
  },

  // Merchants API
  merchants: {
    getAll: async (): Promise<Merchant[]> => {
      const merchants = await merchantsService.getAll();
      return merchants.map(convertSupabaseMerchant);
    },

    getById: async (id: string): Promise<Merchant | null> => {
      const merchant = await merchantsService.getById(id);
      return merchant ? convertSupabaseMerchant(merchant) : null;
    },

    create: async (merchant: any): Promise<Merchant> => {
      const result = await merchantsService.create(merchant);
      if (!result) throw new Error('Failed to create merchant');
      return convertSupabaseMerchant(result);
    },

    update: async (id: string, updates: any): Promise<Merchant> => {
      const result = await merchantsService.update(id, updates);
      if (!result) throw new Error('Failed to update merchant');
      return convertSupabaseMerchant(result);
    },

    delete: async (id: string): Promise<void> => {
      const success = await merchantsService.delete(id);
      if (!success) throw new Error('Failed to delete merchant');
    },

    search: async (query: string): Promise<Merchant[]> => {
      const merchants = await merchantsService.search(query);
      return merchants.map(convertSupabaseMerchant);
    }
  },

  // Items API
  items: {
    getAll: async (): Promise<Item[]> => {
      const items = await itemsService.getAll();
      return items.map(convertSupabaseItem);
    },

    getById: async (id: string): Promise<Item | null> => {
      const item = await itemsService.getById(id);
      return item ? convertSupabaseItem(item) : null;
    },

    create: async (item: any, photos?: File[]): Promise<Item> => {
      const result = await itemsService.create(item, photos);
      if (!result) throw new Error('Failed to create item');
      return convertSupabaseItem(result);
    },

    update: async (id: string, updates: any, newPhotos?: File[], deletePhotos?: string[]): Promise<Item> => {
      const result = await itemsService.update(id, updates, newPhotos, deletePhotos);
      if (!result) throw new Error('Failed to update item');
      return convertSupabaseItem(result);
    },

    delete: async (id: string): Promise<void> => {
      const success = await itemsService.delete(id);
      if (!success) throw new Error('Failed to delete item');
    },

    search: async (query: string): Promise<Item[]> => {
      const items = await itemsService.search(query);
      return items.map(convertSupabaseItem);
    }
  },

  // Categories API
  categories: {
    getAll: async (): Promise<Category[]> => {
      const categories = await categoriesService.getAll();
      return categories.map(convertSupabaseCategory);
    },

    create: async (category: any): Promise<Category> => {
      const result = await categoriesService.create(category);
      if (!result) throw new Error('Failed to create category');
      return convertSupabaseCategory(result);
    },

    update: async (id: string, updates: any): Promise<Category> => {
      const result = await categoriesService.update(id, updates);
      if (!result) throw new Error('Failed to update category');
      return convertSupabaseCategory(result);
    },

    delete: async (id: string): Promise<void> => {
      const success = await categoriesService.delete(id);
      if (!success) throw new Error('Failed to delete category');
    }
  },

  // Locations API
  locations: {
    getAll: async (): Promise<Location[]> => {
      return await locationsService.getAll();
    },

    create: async (location: any): Promise<Location> => {
      const result = await locationsService.create(location);
      if (!result) throw new Error('Failed to create location');
      return result;
    }
  },

  // Users API
  users: {
    getAll: async (): Promise<User[]> => {
      return await usersService.getAll();
    }
  },

  // Dashboard API
  dashboard: {
    getStats: async () => {
      return await dashboardService.getStats();
    }
  }
};

// Export as default API
export const api = supabaseApi;
export default supabaseApi; 